import React, { useState, useEffect } from 'react';
import { crawlingService } from '../../services/crawlingService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';
import ConfirmationModal from '../common/ConfirmationModal';

const CrawlTasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    place_id: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
  });
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'default'
  });

  useEffect(() => {
    loadTasks();
  }, [filters, pagination.page]);

  const loadTasks = async () => {
    setLoading(true);
    const params = {
      page: pagination.page,
      page_size: pagination.page_size,
      ...filters,
    };

    // Remove empty filters
    Object.keys(params).forEach(key => {
      if (params[key] === '') delete params[key];
    });

    const { data, error } = await crawlingService.getCrawlTasks(params);
    
    if (data) {
      setTasks(data.results || []);
      setPagination(prev => ({
        ...prev,
        total: data.count || 0,
      }));
    } else if (error) {
      toast.error('Failed to load crawl tasks');
      console.error('Error loading tasks:', error);
    }
    
    setLoading(false);
  };

  const handleSelectTask = (taskId) => {
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === tasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(tasks.map(task => task.id));
    }
  };

  const cancelSelectedTasks = async () => {
    if (selectedTasks.length === 0) {
      toast.error('Please select tasks to cancel');
      return;
    }

    const pendingTasks = tasks.filter(task => 
      selectedTasks.includes(task.id) && 
      ['pending', 'processing'].includes(task.status)
    );

    if (pendingTasks.length === 0) {
      toast.error('No pending or processing tasks selected');
      return;
    }

    setConfirmationModal({
      isOpen: true,
      title: 'Cancel Tasks',
      message: `Cancel ${pendingTasks.length} selected task(s)?`,
      type: 'warning',
      onConfirm: async () => {
        let successCount = 0;
        let errorCount = 0;

        for (const task of pendingTasks) {
          const { data, error } = await crawlingService.cancelCrawlTask(task.id);
          if (data) {
            successCount++;
          } else {
            errorCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`Cancelled ${successCount} task(s)`);
          loadTasks();
        }
        if (errorCount > 0) {
          toast.error(`Failed to cancel ${errorCount} task(s)`);
        }

        setSelectedTasks([]);
        setConfirmationModal({ ...confirmationModal, isOpen: false });
      }
    });
  };

  const retryFailedTask = async (taskId) => {
    const { data, error } = await crawlingService.createCrawlTask({
      place: tasks.find(t => t.id === taskId)?.place?.id
    });

    if (data) {
      toast.success('Retry task created');
      loadTasks();
    } else {
      toast.error('Failed to create retry task');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getProgressBar = (task) => {
    const percentage = task.progress_percentage || 0;
    const isActive = ['pending', 'processing'].includes(task.status);
    
    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full transition-all duration-300 ${
            isActive ? 'bg-blue-500' : 
            task.status === 'completed' ? 'bg-green-500' :
            task.status === 'failed' ? 'bg-red-500' : 'bg-gray-400'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  const isDomainBlocked = (task) => {
    if (!task.error_message) return false;
    const errorMessage = task.error_message.toLowerCase();
    return errorMessage.includes('crawling not allowed for domain') ||
           errorMessage.includes('robots.txt') ||
           errorMessage.includes('not allowed for domain') ||
           errorMessage.includes('blocked by robots.txt');
  };

  const totalPages = Math.ceil(pagination.total / pagination.page_size);

  if (loading && tasks.length === 0) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
      />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold text-gray-900">Crawl Tasks</h1>
            <div className="flex items-center space-x-3">
              {selectedTasks.length > 0 && (
                <button
                  onClick={cancelSelectedTasks}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  Cancel ({selectedTasks.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="retrying">Retrying</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Place ID
              </label>
              <input
                type="text"
                value={filters.place_id}
                onChange={(e) => setFilters(prev => ({ ...prev, place_id: e.target.value }))}
                placeholder="Filter by place ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({ status: '', place_id: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* List Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={tasks.length > 0 && selectedTasks.length === tasks.length}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-500">
                {selectedTasks.length > 0 ? `${selectedTasks.length} selected` : 'Select all'}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              Page {pagination.page} of {totalPages}
            </div>
          </div>

          {/* Tasks */}
          {tasks.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No crawl tasks found</h3>
              <p className="text-gray-500">Tasks will appear here when places are crawled for social media information.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {tasks.map((task) => (
                <div key={task.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={selectedTasks.includes(task.id)}
                      onChange={() => handleSelectTask(task.id)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    
                    <div className="ml-4 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-base font-medium text-gray-900 flex items-center">
                            {crawlingService.getStatusIcon(task.status)}
                            <span className="ml-2">{task.place?.display_name}</span>
                            <span className={`ml-3 inline-flex px-2 py-1 text-xs font-medium rounded-full ${crawlingService.getStatusColor(task.status)}`}>
                              {task.status_display}
                            </span>
                            {isDomainBlocked(task) && (
                              <span className="ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                🚫 Domain Blocked
                              </span>
                            )}
                          </h4>
                          
                          {task.place?.formatted_address && (
                            <p className="text-sm text-gray-600 mt-1">
                              📍 {task.place.formatted_address}
                            </p>
                          )}
                          
                          {task.current_step && (
                            <p className="text-sm text-gray-600 mt-1">
                              Current step: {task.current_step}
                            </p>
                          )}
                          
                          {task.error_message && (
                            <div className={`text-sm mt-1 p-2 rounded ${
                              isDomainBlocked(task) 
                                ? 'bg-red-50 text-red-700 border border-red-200' 
                                : 'text-red-600'
                            }`}>
                              {isDomainBlocked(task) && <span className="font-medium">Domain Restriction: </span>}
                              {task.error_message}
                            </div>
                          )}
                        </div>
                        
                        <div className="ml-4 flex-shrink-0 text-right">
                          <div className="text-sm text-gray-500">
                            Created: {crawlingService.formatTimestamp(task.created_at)}
                          </div>
                          {task.completed_at && (
                            <div className="text-sm text-gray-500">
                              Completed: {crawlingService.formatTimestamp(task.completed_at)}
                            </div>
                          )}
                          {task.crawl_duration && (
                            <div className="text-sm text-gray-500">
                              Duration: {crawlingService.formatDuration(task.crawl_duration)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">
                            Progress: {task.completed_steps || 0} / {task.total_steps || 0}
                          </span>
                          <span className="text-gray-600">
                            {(task.progress_percentage || 0).toFixed(1)}%
                          </span>
                        </div>
                        {getProgressBar(task)}
                      </div>

                      {/* Task Details */}
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="space-y-1">
                          <div>Priority: {task.priority_display}</div>
                          <div>Retry count: {task.retry_count} / {task.max_retries}</div>
                          {task.discovered_links_count !== undefined && (
                            <div>Links found: {task.discovered_links_count}</div>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          {task.place?.website_uri && (
                            <div>
                              🌐 <a 
                                href={task.place.website_uri} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-600 hover:underline"
                              >
                                Website
                              </a>
                            </div>
                          )}
                          {task.place?.google_place_id && (
                            <div className="text-xs text-gray-400">
                              Google ID: {task.place.google_place_id}
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          {task.celery_task_id && (
                            <div className="text-xs text-gray-400">
                              Task ID: {task.celery_task_id.slice(0, 8)}...
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {task.status === 'failed' && task.can_retry && !isDomainBlocked(task) && (
                            <button
                              onClick={() => retryFailedTask(task.id)}
                              className="px-3 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                            >
                              Retry
                            </button>
                          )}
                          
                          {isDomainBlocked(task) && (
                            <span className="px-3 py-1 bg-red-50 border border-red-200 text-red-700 rounded text-xs">
                              Cannot Retry - Domain Blocked
                            </span>
                          )}
                          
                          {['pending', 'processing'].includes(task.status) && (
                            <button
                              onClick={() => crawlingService.cancelCrawlTask(task.id).then(() => loadTasks())}
                              className="px-3 py-1 bg-red-50 border border-red-200 text-red-700 rounded text-xs hover:bg-red-100 transition-colors"
                            >
                              Cancel
                            </button>
                          )}
                          
                          {task.status === 'completed' && (
                            <button
                              onClick={() => crawlingService.verifyLinks(task.id)}
                              className="px-3 py-1 bg-green-50 border border-green-200 text-green-700 rounded text-xs hover:bg-green-100 transition-colors"
                            >
                              Verify Links
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.page_size) + 1} to{' '}
                {Math.min(pagination.page * pagination.page_size, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                >
                  Previous
                </button>
                
                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + Math.max(1, pagination.page - 2);
                  if (page > totalPages) return null;
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 border rounded text-sm ${
                       page === pagination.page
                         ? 'bg-blue-600 text-white border-blue-600'
                         : 'border-gray-300 hover:bg-gray-50'
                     }`}
                   >
                     {page}
                   </button>
                 );
               })}
               
               <button
                 onClick={() => handlePageChange(pagination.page + 1)}
                 disabled={pagination.page === totalPages}
                 className="px-3 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
               >
                 Next
               </button>
             </div>
           </div>
         )}
       </div>
     </div>
   </div>
 );
};

export default CrawlTasks;